import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Col, Row, Table } from 'antd'
import lodash from 'lodash'
import * as echarts from 'echarts'
import echartsTheme from '@/echartsTheme'
import Chart from './components/Chart'
import useGlobalConfigStore from '@/stores/globalConfig'
import useStationStore from '@/stores/oneStation'
import { FunGroup, StationVirtualPoint } from '@/type/stationType'
import { getDeviceData } from '@/utils/getData'
import MqttServer from '@/utils/mqttUtil'

const TendencyChart: React.FC<{ data: [number, number | string][]; areaColor: string; lineColor: string }> = React.memo(
  ({ data, areaColor, lineColor }) => {
    const chartRef = useRef(null)
    const chartInstance = useRef<any>(null)

    useEffect(() => {
      echarts.registerTheme('customTheme', echartsTheme)
      if (chartRef.current) {
        chartInstance.current = echarts.init(chartRef.current, 'customTheme')
        const option = {
          tooltip: {
            trigger: 'axis',
          },
          grid: {
            top: 10,
            left: 40,
            bottom: 32,
            right: 20,
          },
          legend: {
            show: false,
          },
          xAxis: {
            type: 'time',
            splitNumber: 3,
            axisLabel: {
              fontSize: 12, // Set font size for X-axis labels
              margin: 15, // Set spacing of X-axis labels
            },
          },
          yAxis: {
            axisLabel: {
              fontSize: 12, // Set font size for Y-axis labels
            },
          },
          series: {
            type: 'line',
            data,
            lineStyle: {
              width: 3, // Adjust the line width here
              color: lineColor,
            },
            areaStyle: {
              color: areaColor,
            },
            symbol: 'none', // Remove the symbols (data points)
          },
        }
        chartInstance.current.setOption(option)

        return () => {
          chartInstance.current.dispose()
        }
      }
    }, [])

    useEffect(() => {
      if (data.length > 0 && chartInstance.current) {
        chartInstance.current.setOption({ series: { data } })
      }
    }, [data])

    return <div className='tendencyChart' ref={chartRef} />
  }
)

const Solar: React.FC = () => {
  const { deviceId } = useParams()
  const mqtt_serve = useStationStore(state => state.mqtt_serve)
  const device = useStationStore(state => state.device)
  const station_virtual_label = useStationStore(state => state.station_virtual_label)
  const initFinished = useGlobalConfigStore(state => state.initFinished)
  let targetGroup: FunGroup | undefined,
    mqtt: MqttServer | undefined,
    uuid: string = '',
    id: number = 0
  const [dataTop, setDataTop] = useState<{ label: string; value: string | number; unit: string }[]>([
    { label: "Today's PV generation", value: '0', unit: 'kWh' },
    { label: 'This month PV power', value: '0', unit: 'kWh' },
    { label: 'This year PV power', value: '0', unit: 'kWh' },
    { label: 'Total PV power', value: '0', unit: 'kWh' },
  ])
  const [tableData, setTableData] = useState<{ dc: string; voltage: string; current: string; power: string }[]>([])
  const columns = [
    {
      title: 'DC',
      dataIndex: 'dc',
    },
    {
      title: 'Voltage',
      dataIndex: 'voltage',
    },
    {
      title: 'Current',
      dataIndex: 'current',
    },
    {
      title: 'Power',
      dataIndex: 'power',
    },
  ]
  const [activeChart, setActiveChart] = useState<[number, number | string][]>([])
  const [reactiveChart, setReactiveChart] = useState<[number, number | string][]>([])
  const [chartPoints, setChartPoints] = useState<StationVirtualPoint[]>([])
  const [chartDeviceId, setChartDeviceId] = useState<number>(0)

  let getChartInterval: NodeJS.Timeout
  const getChartData = async () => {
    let res: any
    try {
      res = await getDeviceData(
        [{ device_id: Number(id), defines: ['Dayactive_power', 'Dayreactive_power'] }],
        mqtt as MqttServer,
        uuid
      )
      if (res.code === 1) {
        const currentDate = new Date().getTime()
        setActiveChart(prev => [
          ...prev,
          [
            currentDate,
            res.data[0].datas.find(
              (item: { id: number; property_code: string; value: string }) => item.property_code === 'Dayactive_power'
            )?.value || '0',
          ],
        ])
        setReactiveChart(prev => [
          ...prev,
          [
            currentDate,
            res.data[0].datas.find(
              (item: { id: number; property_code: string; value: string }) => item.property_code === 'Dayreactive_power'
            )?.value || '0',
          ],
        ])
      }
    } catch (e) {
      console.log(e)
    }
  }

  const getData = async () => {
    if (targetGroup) {
      let res: any
      try {
        res = await getDeviceData(
          [{ device_id: Number(id), defines: targetGroup.funs.map(item => item.fun_code) }],
          mqtt as MqttServer,
          uuid
        )
        if (res.code === 1) {
          res.data[0].datas.forEach((item: { id: number; property_code: string; value: string }) => {
            let targetFun = targetGroup?.funs.find(fun => fun.fun_code === item.property_code)
            if (targetFun) {
              targetFun.value = Number(item.value)
            }
          })
          // console.log(targetGroup)
        }
      } catch (e) {
        console.log(e)
      }
      const funs = targetGroup.funs
      const dataTopTemp = [
        {
          label: "Today's PV generation",
          value: funs.find(fun => fun.fun_name === "Today's PV generation")?.value || '0',
          unit: funs.find(fun => fun.fun_name === "Today's PV generation")?.unit || '',
        },
        {
          label: 'This month PV power',
          value: funs.find(fun => fun.fun_name === 'This month PV power')?.value || '0',
          unit: funs.find(fun => fun.fun_name === 'This month PV power')?.unit || '',
        },
        {
          label: 'This year PV power',
          value: funs.find(fun => fun.fun_name === 'This year PV power')?.value || '0',
          unit: funs.find(fun => fun.fun_name === 'This year PV power')?.unit || '',
        },
        {
          label: 'Total PV power',
          value: funs.find(fun => fun.fun_name === 'Total PV power')?.value || '0',
          unit: funs.find(fun => fun.fun_name === 'Total PV power')?.unit || '',
        },
      ]
      const tableDataTemp = [
        {
          dc: 'PV1',
          voltage:
            (funs.find(fun => fun.fun_name === 'MPPT 1 Voltage')?.value.toString() || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 1 Voltage')?.unit || ''),
          current:
            (funs.find(fun => fun.fun_name === 'MPPT 1 Current')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 1 Current')?.unit || ''),
          power:
            (funs.find(fun => fun.fun_name === 'MPPT 1 Power')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 1 Power')?.unit || ''),
        },
        {
          dc: 'PV2',
          voltage:
            (funs.find(fun => fun.fun_name === 'MPPT 2 Voltage')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 2 Voltage')?.unit || ''),
          current:
            (funs.find(fun => fun.fun_name === 'MPPT 2 Current')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 2 Current')?.unit || ''),
          power:
            (funs.find(fun => fun.fun_name === 'MPPT 2 Power')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 2 Power')?.unit || ''),
        },
        {
          dc: 'PV3',
          voltage:
            (funs.find(fun => fun.fun_name === 'MPPT 3 Voltage')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 3 Voltage')?.unit || ''),
          current:
            (funs.find(fun => fun.fun_name === 'MPPT 3 Current')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 3 Current')?.unit || ''),
          power:
            (funs.find(fun => fun.fun_name === 'MPPT 3 Power')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 3 Power')?.unit || ''),
        },
        {
          dc: 'PV4',
          voltage:
            (funs.find(fun => fun.fun_name === 'MPPT 4 Voltage')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 4 Voltage')?.unit || ''),
          current:
            (funs.find(fun => fun.fun_name === 'MPPT 4 Current')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 4 Current')?.unit || ''),
          power:
            (funs.find(fun => fun.fun_name === 'MPPT 4 Power')?.value || '0') +
            (funs.find(fun => fun.fun_name === 'MPPT 4 Power')?.unit || ''),
        },
      ] as any[]
      setDataTop(dataTopTemp)
      setTableData(tableDataTemp)

      const targetLabel = station_virtual_label.find(item => item.type === 'fd')
      if (targetLabel) {
        setChartPoints(
          targetLabel.points.filter(
            item => item.property_code === 'I' || item.property_code === 'KRA' || item.property_code === 'P'
          )
        )
      }
      setChartDeviceId(id)
    }
  }

  useEffect(() => {
    if (initFinished) {
      const targetDevice = lodash.cloneDeep(device.find(item => item.id === Number(deviceId)))
      if (targetDevice) {
        targetGroup = targetDevice.fun_group?.find((item: any) => item.group_name === 'PV')
        mqtt = mqtt_serve[targetDevice.mqtt_server_id]
        uuid = targetDevice.iot_gateway_uuid
        id = targetDevice.device_id
      }
      getData()
      if (getChartInterval) {
        clearInterval(getChartInterval)
      }
      getChartInterval = setInterval(async () => {
        await getChartData()
      }, 5000)
      return () => {
        if (getChartInterval) {
          clearInterval(getChartInterval)
        }
      }
    }
  }, [initFinished])

  return (
    <div id='Solar'>
      <div className='Solar-one'>
        <Row gutter={[16, 16]}>
          {dataTop.map((item, index) => {
            return (
              <Col span={6} key={index}>
                <div className={`Solar-one-box${index % 2 === 0 ? '' : ' Solar-one-box2'}`}>
                  <Row gutter={[15, 15]} align='middle' justify='center'>
                    <Col span={4}>
                      <div className='Solar-one-box-img'>
                        <img src={index % 2 ? '/assets/image/Solarwhite.svg' : `/assets/image/solar.svg`} />
                      </div>
                    </Col>
                    <Col span={20}>
                      <div className='Solar-one-box-value'>
                        <div className='Solar-one-box-value-title'>{item.label}</div>
                        <div className='Solar-one-box-value-number'>
                          <span>{item.value}</span>
                          <span>{item.unit}</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            )
          })}
        </Row>
      </div>
      <div className='Solar-two'>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Table pagination={false} columns={columns} dataSource={tableData} rowKey='dc' />
          </Col>
          <Col span={12}>
            <div className='charts'>
              <div className='chart'>
                <div className='chart-title'>
                  <span>Day_active_power</span>
                  <img src='/assets/image/qp.svg' />
                </div>
                {/* <EChartComponent option={option1} style={{ width: "255px", height: "141.5px" }} /> */}
                <TendencyChart data={activeChart} areaColor='rgba(125,192,88,0.2)' lineColor='green' />
              </div>
              <div className='chart'>
                <div className='chart-title'>
                  <span>Day_reactive_power</span>
                  <img src='/assets/image/qp.svg' />
                </div>
                {/* <EChartComponent option={option2} style={{ width: "255px", height: "142.5px" }} /> */}
                <TendencyChart data={reactiveChart} areaColor='rgba(64,93,249,0.2)' lineColor='#405DF9' />
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <div className='Solar-three'>
        <Chart deviceId={chartDeviceId} points={chartPoints} />
      </div>
    </div>
  )
}

export default Solar

// const SolarTow: React.FC = () => {
//   const data = [
//     { DC: "PV1", Voltage: "12.3V", Current: "1.2A", Power: "14.6W" },
//     { DC: "PV2", Voltage: "12.3V", Current: "1.2A", Power: "14.6W" },
//     { DC: "PV3", Voltage: "12.3V", Current: "1.2A", Power: "14.6W" },
//     { DC: "PV4", Voltage: "12.3V", Current: "1.2A", Power: "14.6W" },
//   ]
//   const columns = [
//     {
//       title: "DC",
//       dataIndex: "DC",
//       key: "DC",
//     },
//     {
//       title: "Voltage",
//       dataIndex: "Voltage",
//       key: "Voltage",
//     },
//     {
//       title: "Current",
//       dataIndex: "Current",
//       key: "Current",
//     },
//     {
//       title: "Power",
//       dataIndex: "Power",
//       key: "Power",
//     },
//   ]
//   const option: echarts.EChartsOption = {
//     tooltip: {
//       trigger: "axis",
//     },
//     grid: {
//       top: 10,
//       left: 40,
//       bottom: 32,
//       right: 20,
//     },
//     legend: {
//       show: false,
//       //   data: ["销量"],
//       bottom: 0, // Legend 距离底部 5%
//       left: "center", // Legend 居中
//       textStyle: {
//         fontSize: 14,
//         color: "#333",
//       },
//     },
//     xAxis: {},
//     yAxis: {
//       axisLabel: {
//         fontSize: 12, // Set font size for Y-axis labels
//       },
//       //   name: "Utilization(10%)",
//       //   nameGap: 25,
//       //   nameTextStyle: {
//       //     color: "#ccc",
//       //     fontSize: 12,
//       //     padding: [0, 0, 0, 42], // 上右下左与原位置距离
//       //   },
//     },
//     series: [
//       {
//         name: "Utilization",
//         type: "line",
//         data: [5, 20, 36, 10, 10, 20],
//         lineStyle: {
//           width: 3, // Adjust the line width here
//         },
//         areaStyle: {},
//         symbol: "none", // Remove the symbols (data points)
//       },
//     ],
//   }
//   const option2: echarts.EChartsOption = {
//     ...option,
//     xAxis: {
//       boundaryGap: false,

//       data: ["00:00", "00:10", "00:20", "00:30", "00:40", "00:50"],
//       axisLabel: {
//         fontSize: "0.7em", // Set font size for X-axis labels
//         margin: 15, // Set spacing of X-axis labels
//       },
//     },
//     series: [
//       {
//         type: "line",
//         data: [5, 20, 36, 10, 10, 20],
//         lineStyle: {
//           width: 3, // Adjust the line width here
//         },

//         areaStyle: {
//           color: "rgba(125,192,88,0.2)",
//         },
//         symbol: "none", // Remove the symbols (data points)
//       },
//     ],
//   }

//   const option1: echarts.EChartsOption = {
//     ...option,
//     xAxis: {
//       data: ["00:00", "00:10", "00:20", "00:30", "00:40", "00:50"],
//       boundaryGap: false,

//       axisLabel: {
//         fontSize: "0.7em", // Set font size for X-axis labels
//         margin: 15, // Set spacing of X-axis labels
//       },
//     },
//     series: [
//       {
//         type: "line",
//         data: [5, 20, 36, 10, 10, 20],
//         lineStyle: {
//           color: "#405DF9", // Line color

//           width: 3, // Adjust the line width here
//         },
//         areaStyle: {
//           color: "rgba(64,93,249,0.2)",
//         },
//         symbol: "none", // Remove the symbols (data points)
//       },
//     ],
//   }
//   return (
//     <Row gutter={[16, 16]}>
//       <Col span={12}>
//         <Table pagination={false} columns={columns} dataSource={data} />
//       </Col>
//       <Col span={12}>
//         <div className="charts">
//           <div className="chart">
//             <div className="chart-title">
//               <span>Day_active_power</span>
//               <img src="/assets/image/qp.svg" />
//             </div>
//             <EChartComponent option={option1} style={{ width: "100%", height: "189.5px" }} />
//           </div>
//           <div className="chart">
//             <div className="chart-title">
//               <span>Day_reactive_power</span>
//               <img src="/assets/image/qp.svg" />
//             </div>
//             <EChartComponent option={option2} style={{ width: "100%", height: "189.5px" }} />
//           </div>
//         </div>
//       </Col>
//     </Row>
//   )
// }
